import CustomerFooter from "./CustomerFooter";
import "../ReportTable.css";
import HeaderCustomer from "./HeaderCustomer";
import './Customer.css';
import { useState } from "react";
import { Table } from "react-bootstrap";
import ApiHandler from "../../ApiHandler";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import LoaderTri from "../Loader/LoaderTri";
import { toast } from "react-toastify";
import FirmSelect from './FirmSelect';
import { getReportFirmId, getReportFirmLocalUrl, getReportFirmName } from '../../ClientSelection';
import { dateField } from "../../dateOnly";
import ReportExport from "../ReportExport";

/**
 * Payment Receipts - the receipt vouchers raised against this customer.
 *
 * Built on the Download Invoice screen: same firm dropdown, same date range,
 * same request body (`startdate` / `enddate` / `code` / `wClientId`), the same
 * report table and the same Excel / PDF downloads. GeCustomerRVList returns a
 * flat list - `{ type, date, vouNo, amount }` - and each row carries a .pdf
 * button that asks CustomerRVDownload for that one receipt.
 */

const initialValues = {
    from: dayjs().startOf('month'),
    to: dayjs()
};

export default function PaymentReceipts() {
    const [dates, setDates] = useState(initialValues);
    const [showReceipts, setShowReceipts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [viewdata, setViewdata] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    // The firm whose server these receipts come from. Held explicitly so the
    // request cannot silently fall back to the login firm's cached LocalURL.
    const [firm, setFirm] = useState(null);

    // NOTE: this is wired straight to onClick, so the first argument may be a
    // click event. Only accept a string url.
    function toGetData(overrideUrl) {
        const explicitUrl = typeof overrideUrl === "string" ? overrideUrl : null;
        const localUrl = explicitUrl || (firm && firm.localUrl) || getReportFirmLocalUrl();
        const clientId = (firm && firm.wClientId) || getReportFirmId();

        if (!localUrl) {
            toast.error("No server configured for the selected firm");
            return;
        }

        const user = JSON.parse(localStorage.getItem('user-info'));

        setViewdata(true);
        setIsLoading(true);
        setShowReceipts([]);

        ApiHandler.post({
            url: localUrl + "api/OwnApp/GeCustomerRVList",
            data: {
                startdate: dayjs(dates.from).format("YYYYMMDD"),
                enddate: dayjs(dates.to).format("YYYYMMDD"),
                // the customer code, as the other OwnApp reports send it
                code: user.code,
                // the firm from the dropdown, as its own field
                wClientId: clientId,
            },
            useUserUrl: true
        })
            .then(((result) => {
                setShowReceipts(Array.isArray(result.data) ? result.data : []);
            }))
            .catch(err => {
                console.error(err);
                if (err.response && err.response.status && err.response.status === 401) {
                    console.log(err.response);
                }
                toast.error("Could not load the payment receipts");
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    // Record the firm, then refetch for it. `firm` state has not updated yet at
    // this point, so the new url is passed straight through to toGetData.
    const handleFirmChange = (picked) => {
        setFirm(picked || null);
        setShowReceipts([]);

        const url = picked && picked.localUrl;

        if (viewdata && url) {
            toGetData(url);
        }
    };

    const reportRange = dayjs(dates.from).format("DD-MM-YYYY") + " to " +
        dayjs(dates.to).format("DD-MM-YYYY");

    const exportColumns = [
        { key: "type", label: "Type" },
        { key: "date", label: "Receipt Date", format: (v, row) => dateField(row, "date", "rvDate") },
        { key: "vouNo", label: "Voucher No", align: "right" },
        { key: "amount", label: "Amount", align: "right" },
    ];

    /* totalled, because the first thing anyone asks of a receipts list is how
       much was paid over the period */
    const total = showReceipts.reduce((sum, elm) => {
        const value = Number(String(elm.amount).replace(/,/g, ""));

        return sum + (isNaN(value) ? 0 : value);
    }, 0);

    const money = (value) => {
        const number = Number(String(value).replace(/,/g, ""));

        return isNaN(number) ? value : number.toLocaleString('en-IN');
    };

    /* CustomerRVDownload writes the file on the firm's own server and answers
       with the relative path to it - "Bills\\300287_454_20260902.pdf". Windows
       separators, and no host, so both are patched in before the browser is
       sent there - the same trick the invoice screen uses for its own file. */
    const downloadReceiptPdf = (receipt) => {
        const user = JSON.parse(localStorage.getItem('user-info'));
        // the firm from the dropdown decides which server holds the file
        const localUrl = (firm && firm.localUrl) || getReportFirmLocalUrl();
        const clientId = (firm && firm.wClientId) || getReportFirmId();

        if (!localUrl) {
            toast.error("No server configured for the selected firm");
            return;
        }

        // the list gives DD/MM/YYYY, this endpoint wants YYYYMMDD
        const formattedDate = dayjs(receipt.date, "DD/MM/YYYY").format("YYYYMMDD");

        setIsDownloading(true);

        ApiHandler.post({
            url: localUrl + "api/OwnApp/CustomerRVDownload",
            data: {
                type: receipt.type,
                billno: `${receipt.vouNo}`,
                date: formattedDate,
                custCode: `${user.code}`,
                // the firm from the dropdown, as its own field
                wClientId: clientId,
                fileType: "pdf"
            },
            useUserUrl: true
        })
            .then((result) => {
                // the path may come back bare or wrapped in an object
                const answer = result.data;
                const raw = typeof answer === "string"
                    ? answer
                    : (answer && (answer.filePath || answer.path || answer.fileName)) || "";

                const relative = String(raw).replace(/\\/g, "/").replace(/^\/+/, "");

                if (!relative) {
                    toast.error("The server did not return a receipt file");
                    return;
                }

                const base = localUrl.endsWith("/") ? localUrl : localUrl + "/";

                window.location.href = base + relative;
            })
            .catch(err => {
                console.error(err);
                toast.error("Could not download the receipt");
            })
            .finally(() => {
                setIsDownloading(false);
            });
    };

    return (
        <div className="custShell">
            <HeaderCustomer />

            <div className="custMain">
                <div className="row m-0">
                    <div className="col-lg-10 offset-lg-1">
                        <h5 style={{ marginTop: "20px", fontFamily: "sans-serif" }}>
                            Select details to view Payment Receipts
                        </h5>
                    </div>

                    <div className="col-lg-10 offset-lg-1">
                        <div className="row">
                            <div className="col-lg-3  ">
                                <FirmSelect
                                    onReady={(picked) => setFirm(picked || null)}
                                    onChange={handleFirmChange}
                                />
                            </div>

                            <div className="col-lg-3  ">
                                <DatePicker.RangePicker
                                    format={"DD-MM-YYYY"}
                                    size="middle"
                                    className="mb-2"
                                    value={[dates.from, dates.to]}
                                    onChange={(e) => {
                                        if (e) {
                                            setDates({ from: e[0], to: e[1] });
                                        } else {
                                            setDates({ from: null, to: null });
                                        }
                                    }}
                                />
                            </div>

                            <div className="col-lg-2  ">
                                <button
                                    type="button"
                                    className="btn mb-2 btn-primary btn-sm"
                                    onClick={() => toGetData()}
                                >View</button>
                            </div>

                            {/* the downloads sit on this line, right-aligned */}
                            <div className="col-lg-4">
                                <ReportExport
                                    className="rptExportEnd"
                                    title="Payment Receipts"
                                    subtitle={[getReportFirmName(), reportRange]}
                                    columns={exportColumns}
                                    rows={showReceipts}
                                    fileName="payment-receipts"
                                />
                            </div>
                        </div>
                    </div>

                    {viewdata &&
                        <div className="row m-0">
                            <div className="p-0 col-lg-10 offset-lg-1">
                                <h6>
                                    Payment Receipts
                                    {isDownloading &&
                                        <span style={{ marginLeft: "10px", fontWeight: 400 }}>
                                            Downloading...!
                                        </span>
                                    }
                                </h6>

                                <div className="rptScroll">
                                    <Table striped className="rptTable">
                                        <thead className="threAd">
                                            <tr className="threAd_tr">
                                                <th>Type</th>
                                                <th>Receipt Date</th>
                                                <th className="rptAmt">Voucher No</th>
                                                <th className="rptAmt rptCredit">Amount</th>
                                                <th style={{ textAlign: 'center' }}>Download</th>
                                            </tr>
                                        </thead>

                                        <tbody className="tboDY">
                                            {showReceipts.map((elm, index) => (
                                                <tr key={index}>
                                                    <td>{elm.type}</td>
                                                    <td>{dateField(elm, 'date', 'rvDate')}</td>
                                                    <td className="rptAmt">{elm.vouNo}</td>
                                                    <td className="rptAmt rptCredit">{money(elm.amount)}</td>
                                                    <td className="BTNs">
                                                        <button
                                                            type="button"
                                                            className="invBtn pdfBTN"
                                                            disabled={isDownloading}
                                                            onClick={() => downloadReceiptPdf(elm)}
                                                            title={"Download receipt " + elm.vouNo + " as a PDF to print or e-mail"}
                                                        >
                                                            <span className="invBtnExt">.pdf</span>
                                                            <span className="invBtnName">Print</span>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}

                                            {showReceipts.length > 0 &&
                                                <tr className="rptTotalRow">
                                                    <td colSpan={3}>
                                                        Total &mdash; {showReceipts.length}
                                                        {showReceipts.length === 1 ? " receipt" : " receipts"}
                                                    </td>
                                                    <td className="rptAmt">{money(total)}</td>
                                                    <td></td>
                                                </tr>
                                            }
                                        </tbody>
                                    </Table>
                                </div>

                                {!isLoading && showReceipts.length === 0 &&
                                    <p className="rptEmpty">
                                        No payment receipts in this period.
                                    </p>
                                }

                                {isLoading &&
                                    <div className="d-flex justify-content-center">
                                        <LoaderTri />
                                    </div>
                                }
                            </div>
                        </div>
                    }
                </div>
            </div>

            <CustomerFooter />
        </div>
    );
}
